import { SkeletonItem } from 'components/reusable/SkeletonItem'
import {
  type QuotingInvitedPartnerAdmin,
  type ResourceAccessTokensResourceAccessTokenAbilitiesItem,
  type ResourceAccessTokensResourceAccessTokenRecipient as ResourceAccessTokenRecipient,
} from 'utils/web_api.schemas'

import { PartnerAdminRow } from './PartnerAdminRow'
import { TokenRecipientRow } from './TokenRecipientRow'

type RecipientsListProps = {
  recipients: ResourceAccessTokenRecipient[] | undefined,
  partnerAdminRelationships: QuotingInvitedPartnerAdmin[] | undefined,
  isLoading: boolean,
  invitingEmail: string | null,
  copyingLinkForEmail: string | null,
  revokingRecipientId: string | null,
  onResendInvite: (email: string, abilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[]) => void,
  onCopyLink: (tokenId: string | null, recipient: ResourceAccessTokenRecipient) => void,
  onEditPermissions: (recipientId: string, abilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[]) => void,
  onRevokeAccess: (recipientId: string) => void,
  canEditQuote: boolean,
  canSendEditorInvites: boolean,
  canSendOnboardInvites: boolean
}

export const RecipientsList = ({
  recipients,
  partnerAdminRelationships,
  isLoading,
  invitingEmail,
  copyingLinkForEmail,
  revokingRecipientId,
  onResendInvite,
  onCopyLink,
  onEditPermissions,
  onRevokeAccess,
  canEditQuote,
  canSendEditorInvites,
  canSendOnboardInvites,
}: RecipientsListProps) => {
  if (isLoading) {
    return <SkeletonItem heightClass='h-44' />
  }

  if (!recipients?.length && !partnerAdminRelationships?.length) {
    return (
      <div className='text-md flex items-center justify-center h-44'>
        No one has been invited yet.
      </div>
    )
  }

  return (
    <div className='max-h-72 overflow-y-auto'>
      {recipients?.map((recipient) => (
        <TokenRecipientRow
          key={recipient.id}
          recipient={recipient}
          isInviting={invitingEmail === recipient.email}
          isCopyingLink={copyingLinkForEmail === recipient.email}
          isRevoking={revokingRecipientId === recipient.id}
          onResendInvite={abilities => onResendInvite(recipient.email, abilities)}
          onCopyLink={tokenId => onCopyLink(tokenId, recipient)}
          onEditPermissions={onEditPermissions}
          onRevokeAccess={onRevokeAccess}
          canEditQuote={canEditQuote}
          canSendEditorInvites={canSendEditorInvites}
          canSendOnboardInvites={canSendOnboardInvites}
        />
      ))}
      {partnerAdminRelationships?.map(({ partner_admin: partnerAdmin, role }) =>
        <PartnerAdminRow key={partnerAdmin.id} partnerAdmin={partnerAdmin} role={role} />
      )}
    </div>
  )
}
